import { getVerbSettings } from '../sqlform';
import { Rule } from './index';

/**
 * Whether a rule can be sent to the server without breaking on it.
 *
 * `setOverrides` validates overrides by RUNNING them, so an unfinished rule does
 * not fail politely: it reaches Postgres, throws (`invalid input syntax for type
 * numeric: ""` for an empty complement against a numeric column), and the whole
 * save rolls back, taking the finished rules in the same request with it.
 *
 * That did not matter while a person pressed Save when they were done. It
 * matters now that the page saves on its own, because a rule is unfinished for
 * the entire time it is being written, and it is created unfinished: a fresh
 * rule has one condition with an empty complement.
 *
 * Completeness is read off the verb model rather than assumed. `is known` and
 * `is unknown` take no complement at all (`min: 0`), so "has an empty
 * complement" is not the same question as "is unfinished", and treating them as
 * one would make those two rules permanently unsavable.
 */
export function isRuleComplete(rule: Rule): boolean {
  if (rule.ruleType === 'advanced') {
    return Boolean(rule.sqlConditions && rule.sqlConditions.trim());
  }

  const conditions = rule.formConditions ?? [];
  // A form rule with no conditions at all matches everything, which is never
  // what someone means and is destructive if saved.
  if (!conditions.length) return false;

  return conditions.every((condition) => {
    if (!condition?.subject?.subject || !condition.verb) return false;
    // A verb the settings table does not know defaults to needing NOTHING, not
    // to needing one. The other way round, a verb that legitimately takes no
    // complement and is missing from the table makes its rule permanently
    // incomplete, and since one incomplete rule holds the whole write, the page
    // becomes unsavable with no way out. `setOverrides` validates by executing
    // the SQL anyway, so the server is the authority here and this is only a
    // guard against sending a rule that is obviously half-typed.
    const required = getVerbSettings(condition.verb)?.characteristics?.min ?? 0;
    const supplied = (condition.complements ?? []).filter(
      (complement) => complement !== null && String(complement).trim() !== ''
    ).length;
    return supplied >= required;
  });
}

/** The rules that cannot be saved yet, by position. */
export function incompleteRuleIndexes(rules: Rule[]): number[] {
  return rules.reduce<number[]>((out, rule, index) => {
    if (!isRuleComplete(rule)) out.push(index);
    return out;
  }, []);
}
